import React, { useEffect, useState } from "react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Eye, Download, Contact, TrendingUp } from "lucide-react";
import api from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";

const Funnel = ({ icon: Icon, label, value, pct }) => (
  <div className="iw-card p-6" data-testid={`funnel-${label.toLowerCase()}`}>
    <div className="flex items-center justify-between">
      <span className="overline">{label}</span>
      <Icon size={18} className="text-[#D4AF37]" strokeWidth={1.5} />
    </div>
    <p className="font-display text-4xl font-bold mt-3">{value}</p>
    {pct != null && <p className="text-xs text-[#8B9BB4] mt-1">{pct}% conversion</p>}
  </div>
);

export default function Analytics() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/analytics").then((r) => setData(r.data)); }, []);

  if (!data) return <DashboardLayout title="Analytics"><p className="text-[#8B9BB4]">Loading…</p></DashboardLayout>;

  const savePct = data.views ? Math.round((data.saves / data.views) * 100) : 0;
  const leadPct = data.views ? Math.round((data.leads / data.views) * 100) : 0;

  return (
    <DashboardLayout title="Analytics">
      <div className="fade-up space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Funnel icon={Eye} label="Views" value={data.views} />
          <Funnel icon={Download} label="Saves" value={data.saves} pct={savePct} />
          <Funnel icon={Contact} label="Leads" value={data.leads} pct={leadPct} />
        </div>

        <div className="iw-card p-6">
          <p className="overline flex items-center gap-2"><TrendingUp size={14} /> Views — last 14 days</p>
          <div className="h-72 mt-6" data-testid="views-chart">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.series} margin={{ left: -20, right: 10 }}>
                <defs>
                  <linearGradient id="gold" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#D4AF37" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#D4AF37" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#ffffff12" vertical={false} />
                <XAxis dataKey="date" stroke="#8B9BB4" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="#8B9BB4" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "#0B1D3A", border: "1px solid #D4AF3733", borderRadius: 8, color: "#F6F1E4" }} />
                <Area type="monotone" dataKey="views" stroke="#D4AF37" strokeWidth={2} fill="url(#gold)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="iw-card overflow-hidden">
          <p className="overline p-6 pb-3">Per-card performance</p>
          {data.per_card.length === 0 ? <p className="px-6 pb-6 text-[#8B9BB4] text-sm">No cards yet.</p> : (
            <table className="w-full text-sm" data-testid="per-card-table">
              <thead className="bg-[#142B5A] text-[#8B9BB4]">
                <tr>
                  <th className="text-left px-6 py-3 font-semibold">Card</th>
                  <th className="text-right px-6 py-3 font-semibold">Views</th>
                  <th className="text-right px-6 py-3 font-semibold">Saves</th>
                  <th className="text-right px-6 py-3 font-semibold">Leads</th>
                </tr>
              </thead>
              <tbody>
                {data.per_card.map((c) => (
                  <tr key={c.id} className="border-t border-[#ffffff1a]">
                    <td className="px-6 py-3 text-[#F6F1E4] font-medium">{c.name}</td>
                    <td className="px-6 py-3 text-right text-[#8B9BB4]">{c.views}</td>
                    <td className="px-6 py-3 text-right text-[#8B9BB4]">{c.saves}</td>
                    <td className="px-6 py-3 text-right text-[#D4AF37] font-semibold">{c.leads}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {data.per_department?.length > 0 && (
          <div className="iw-card p-6" data-testid="dept-analytics">
            <p className="overline mb-5">Networking by department</p>
            <div className="space-y-4">
              {(() => { const max = Math.max(...data.per_department.map((d) => d.views), 1); return data.per_department.map((d) => (
                <div key={d.department} data-testid={`dept-row-${d.department}`}>
                  <div className="flex items-center justify-between text-sm mb-1.5">
                    <span className="text-[#F6F1E4] font-medium">{d.department} <span className="text-[#8B9BB4] font-normal">· {d.cards} card{d.cards !== 1 ? "s" : ""}</span></span>
                    <span className="text-[#8B9BB4]">{d.views} views · {d.saves} saves · <span className="text-[#D4AF37] font-semibold">{d.leads} leads</span></span>
                  </div>
                  <div className="h-2 rounded-full bg-[#142B5A] overflow-hidden">
                    <div className="h-full bg-[#D4AF37] transition-all" style={{ width: `${(d.views / max) * 100}%` }} />
                  </div>
                </div>
              )); })()}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
